import React from "react";
import LeaveDayCard from "../cards/LeaveDayCard";
import { LeaveDuration, LeaveDurationNames } from "../../../types/common-types";
import { UserAttendanceSummary } from "../../../apis/performance/attendance.api";
import { getFirstCharacter } from "../../../utils/helper";
import { formatMonthYear } from "../../../utils/date-format";

interface AttendanceSummaryProps {
  data: UserAttendanceSummary | null;
}

interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const SummaryRow: React.FC<SummaryRowProps> = ({
  label,
  value,
  children,
  className = "",
}) => (
  <div
    className={`grid gap-2 border-b border-gray-200 py-2 grid-cols-[100px_minmax(0,1fr)] sm:grid-cols-[150px_minmax(0,1fr)] items-center sm:gap-2 ${className}`}
  >
    <div className="text-xs font-normal text-gray-500 sm:text-[13px]">
      {label}
    </div>

    <div className="flex items-center gap-2">
      <span className="text-sm font-semibold text-black min-w-[25px]">
        {value}
      </span>

      {children}
    </div>
  </div>
);

const AttendanceSummary: React.FC<AttendanceSummaryProps> = ({ data }) => {
  if (!data) {
    return <div className="w-full bg-white py-4 text-sm text-gray-500">Summary unavailable.</div>;
  }

  const { period, policy, summary } = data;
  const monthLabel = formatMonthYear(period.month, period.year);
  const getDay = (date: string) => {
    const dayOffset = Math.round(
      (Date.parse(date) - Date.parse(period.startDate)) / 86400000,
    );
    return dayOffset + 1;
  };
  const weekOffs = summary.weekOffRecord.map((record) => getDay(record.attendanceDate));
  const holidays = summary.holidayRecord.map((record) => getDay(record.attendanceDate));
  const leaveDays = summary.leaveRecord.map((record) => ({
    day: getDay(record.attendanceDate),
    code: getFirstCharacter(record.leaveRequestId?.leaveId?.name??"", 3),
    type:
      LeaveDurationNames[
        (record.leaveRequestId?.duration as LeaveDuration) || LeaveDuration.FULL_DAY
      ],
  }));
  const absentDays = summary.absentRecord
    .map((record) => ({
      day: getDay(record.attendanceDate),
      code: "A",
      type: record.isHalfDay ? LeaveDurationNames.HALF_DAY : LeaveDurationNames.FULL_DAY,
    }));

  return (
    <div className="w-full bg-white">
      <SummaryRow
        label="Policy Name"
        value={policy.policyId.name}
      />

      <SummaryRow
        label="Total days"
        value={period.monthDays}
      >
        <span className="text-xs text-gray-400">
          ({monthLabel})
        </span>
      </SummaryRow>

      <SummaryRow
        label="Working days"
        value={summary.workingDays}
      />

      <SummaryRow
        label="Weekoff"
        value={summary.weekOffDays}
      >
        <div className="flex flex-wrap gap-2">
          {weekOffs.map((day) => (
            <div
              key={day}
              className="flex h-7 min-w-7 items-center justify-center bg-warning/20 px-2 text-xs text-gray-600"
            >
              {day}
            </div>
          ))}
        </div>
      </SummaryRow>

      <SummaryRow
        label="Holidays"
        value={summary.holidayDays}
      >
        <div className="flex flex-wrap gap-2">
          {holidays.map((day) => (
            <div
              key={day}
              className="flex h-7 min-w-7 items-center justify-center bg-warning/20 px-2 text-xs text-gray-600"
            >
              {day}
            </div>
          ))}
        </div>
      </SummaryRow>

      <SummaryRow
        label="Present days"
        value={summary.presentDays}
      />

      <SummaryRow
        label="Leave days"
        value={summary.leaveDays}
        className="py-3 sm:py-4"
      >
        <div className="flex flex-wrap gap-2">
          {leaveDays.map((leave) => (
            <LeaveDayCard
              key={`${leave.day}-${leave.code}`}
              day={leave.day}
              code={leave.code}
              type={leave.type}
            />
          ))}
        </div>
      </SummaryRow>

      <SummaryRow
        label="Absent days"
        value={summary.absentDays}
        className="py-3 sm:py-4"
      >
        <div className="flex flex-wrap gap-2">
          {absentDays.map((absence) => (
            <LeaveDayCard
              key={`${absence.day}-${absence.code}`}
              day={absence.day}
              code={absence.code}
              type={absence.type}
              variant="absent"
            />
          ))}
        </div>
      </SummaryRow>

      <SummaryRow
        label="Payable Days"
        value={summary.payableDays}
      />
    </div>
  );
};

export default AttendanceSummary;